import { IProduct } from '@boma/website/shared/entities';
import { IOrganization } from '@boma/client-shared';
import { ICustomer, ILocation } from '@boma/supplier-portal/shared/entities';

export interface IPricingGroupCreate {
  entityId: string;
  name: string;
  filters: IPricingGroupFilters;
  defaultSettings: IPricingGroupSettings;
}

export interface IPricingGroupUpdate {
  id?: string;
  entityId: string;
  name: string;
  customerIds: string[];
  pricing: IPricingProduct[];
  defaultSettings: IPricingGroupSettings;
  // If boolean; overwrites all pricing tiers in this group with the given pricing tiers
  replacePricingTiers: boolean;
}

export interface IPricingGroupFilters {
  productIds: string[];
  locationIds: string[];
  customerIds: string[];
  categoryIds: string[];
  departmentIds: string[];
  brandIds: string[];
  definitionsFilters: IPricingGroupDefinitions;
}

export interface IPricingGroupDefinitions {
  targets: number;
  definitionIds: string[];
}

export interface IPricingGroupSettings {
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom: string;
  validUntil: string;
}

export interface IPricingLocationPricingTier {
  id: string;
  userId?: string;
  entityId?: string;
  productId: string;
  price: number;
  newPrice?: number;
  minimumPrice?: number;
  locationId: string;
  locationName?: string;
  stockLocationId: string;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  update: boolean;
  status?: string;
  selected?: boolean;
  bulk?: {
    newPrice?: number;
    percentage?: number;
    fixedPrice?: number;
  };
}

export interface IPricingLocationPricingTierSave {
  id: string;
  productId: string;
  price: number;
  locationId: string;
  stockLocationId: string;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  update: boolean;
}

export interface IPricingLocationPricingTierResponse {
  product: IProduct;
  pricingTiers: IPricingTier[];
}
export interface IPricingProduct {
  id: string;
  entityId: string;
  productId: string;
  locationId: string;
  price: string;
  minimumOrderQuantity: number;
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  maximumOrderQuantity: number;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom: string;
  validUntil: string;
}

export interface IPricingGroupByLocationProducts {
  id: string;
  entity: IOrganization;
  name: string;
  customers: ICustomer[];
  items: {
    location: ILocation;
    products: IPricingGroupByLocationProductItem[];
  }[];
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom: string;
  validUntil: string;
}

export interface IPricingUser {
  id: string;
  firstName: string;
  surname: string;
}

export interface IPricingGroupByLocationProductItem {
  user: IPricingUser;
  lastUpdatedBy: IPricingUser;
  price: number;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  product: IProduct;
}

export interface IPricingBulkProduct {
  bulk?: {
    newPrice?: number;
    percentage?: number;
    fixedPrice?: number;
  };
  updateAll: boolean;
  product: IProduct;
  locationPricing: IPricingBulkProductLocation[];
}

export interface IPricingTierList {
  locationIds: string[];
  productIds: string[];
  customerIds: string[];
  groupBy: string;
  deletedEntries: boolean;
}

export interface IPricingCustomerProductsWithPricingTier {
  locationPricing?: any;
  pricingTiers?: any;
  items?: any;
  entityCustomerLocationProductPrices: any;
  customer: ICustomer;
  productsWithPricingTiers: {
    product: IProduct;
    locations?: ILocation[];
    pricingTiers: IPricingTier[]; // TODO create pricing tier interface
  }[];
}

export interface IPricingCustomerLocationPricing {
  product: {
    id: string;
    name: string;
  };
  locationPricing: ILocation;
  pricingTiers: IPricingTier[];
}

export interface IPricingTier {
  pricingHistory?: any;
  stockLocation?: ILocation;
  location?: ILocation;
  customerLocation?: ILocation;
  pricingTierId: string;
  pricingGroupId: string;
  pricingGroupName: string;
  customers: ICustomer[];
  user: any;
  lastUpdated?: any;
  lastUpdatedBy: any;
  deletedAt?: any;
  price?: number;
  minimumPrice?: number;
  minimumOrderQuantity?: number;
  maximumOrderQuantity?: number;
}

export interface IPricingBulkProductLocation {
  location: {
    id: string;
    name: string;
    type: string;
  };
  updateAll?: boolean;
  updated: boolean;
  price: number;
  newPrice?: number;
  cost: number;
  isCurrentlyBeingSold: boolean;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  externalSalesEnabled: boolean;
  internalSalesEnabled: boolean;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  internalNegativeStockSalesAllowed: boolean;
  externalNegativeStockSalesAllowed: boolean;
  minimumEstimatedDaysToDeliver: number;
  maximumEstimatedDaysToDeliver: number;
}
export interface IPricingLocationProductSettingsItem {
  locationId: string;
  stockLocationId: string;
  price: number;
  cost: number;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  acceptElectronicPayments: boolean;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  externalSalesEnabled: boolean;
  internalSalesEnabled: boolean;
  enforceStockChecks: boolean;
  confirmAllExternalOrders: boolean;
  internalNegativeStockSalesAllowed: boolean;
  externalNegativeStockSalesAllowed: boolean;
  minimumEstimatedDaysToDeliver: number;
  maximumEstimatedDaysToDeliver: number;
}

export interface IPricingCustomerPricingTier {
  id: string;
  customerEntityId: string;
  customerLocationId: string;
  price: number;
  locationId: string;
  stockLocationId: string;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
}

export interface IPricingBulkUpdateItem {
  productId: string;
  locationProductSettings: IPricingLocationProductSettingsItem[];
  customerPricingTiers: IPricingCustomerPricingTier[];
}
